import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import type { EditorState, ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { willComponentRender } from '@atlaskit/editor-ui-control-model/surface-renderer';
import type { ResolvedSurface } from '@atlaskit/editor-ui-control-model/surface-renderer/types';
import { createSurfaceContext } from '@atlaskit/editor-ui-control-model/types';

import type { ActiveNode } from '../../blockControlsPluginType';
import { createBlockControlsSurfaceContextForPosition } from '../../ui/block-controls-surface-context';

const createSurfaceContextAtPosition = (
	state: EditorState,
	position: number,
	activeNode?: ActiveNode,
) => {
	const blockControlsContext = createBlockControlsSurfaceContextForPosition(
		state,
		position,
		activeNode,
	);
	return blockControlsContext
		? createSurfaceContext(BLOCK_CONTROL_UI_CONTEXT, blockControlsContext)
		: undefined;
};

export const getSurfaceNodePositions = (
	state: EditorState,
	resolvedSurface: ResolvedSurface,
	activeNode?: ActiveNode,
	from = 0,
	to: number = state.doc.content.size,
): number[] => {
	const { root, childrenMap } = resolvedSurface;
	if (!root) {
		return [];
	}

	const positions: number[] = [];
	const start = Math.max(0, from);
	const end = Math.min(state.doc.content.size, Math.max(start, to));

	state.doc.nodesBetween(start, end, (node, position) => {
		if (
			node.isBlock &&
			willComponentRender(
				root,
				childrenMap,
				createSurfaceContextAtPosition(state, position, activeNode),
			)
		) {
			positions.push(position);
		}
	});

	return positions;
};

const hasActiveNodeChanged = (
	previousActiveNode: ActiveNode | null | undefined,
	activeNode: ActiveNode | null | undefined,
): boolean =>
	previousActiveNode?.pos !== activeNode?.pos ||
	previousActiveNode?.nodeType !== activeNode?.nodeType ||
	previousActiveNode?.rootPos !== activeNode?.rootPos;

export const updateSurfaceNodePositions = ({
	activeNode,
	currentPositions,
	from,
	newState,
	previousActiveNode,
	resolvedSurface,
	to,
	tr,
}: {
	activeNode: ActiveNode | null | undefined;
	currentPositions: number[];
	from: number;
	newState: EditorState;
	previousActiveNode: ActiveNode | null | undefined;
	resolvedSurface: ResolvedSurface;
	to: number;
	tr: ReadonlyTransaction;
}): number[] => {
	const activeNodeChanged = hasActiveNodeChanged(previousActiveNode, activeNode);
	if (!tr.docChanged && !activeNodeChanged) {
		return currentPositions;
	}

	const nextActiveNode = activeNode ?? undefined;
	if (activeNodeChanged) {
		return getSurfaceNodePositions(newState, resolvedSurface, nextActiveNode);
	}

	const { root, childrenMap } = resolvedSurface;
	const mappedPositions = currentPositions
		.map((position) => tr.mapping.mapResult(position, -1))
		.filter((result) => !result.deleted)
		.map((result) => result.pos)
		.filter((position) => {
			const node = newState.doc.nodeAt(position);
			return Boolean(
				node?.isBlock &&
				root &&
				willComponentRender(
					root,
					childrenMap,
					createSurfaceContextAtPosition(newState, position, nextActiveNode),
				),
			);
		});
	const changedPositions = getSurfaceNodePositions(
		newState,
		resolvedSurface,
		nextActiveNode,
		from - 1,
		to + 1,
	);

	return Array.from(new Set([...mappedPositions, ...changedPositions])).sort(
		(first, second) => first - second,
	);
};
